// IM-XPR-002 (IM-UI-096, IM-UX-112) — Páginas para acompanhar: poucas recomendações prioritárias com o motivo e o impacto
// esperado, ativação direta e as demais em "Ver outras sugestões", agrupadas por empresa. A ativação usa as rotas
// donas (IM-API-253 ativar recomendação; IM-API-171 monitorar pelo battlecard), que aplicam tenancy, RBAC (ADMIN+),
// deduplicação, periodicidade e auditoria. Rede social nunca é ativada por aqui: depende de autorização da rede.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useToast } from '../components/Toast';
import { battlecardApi } from '../data/battlecards';
import { PAGE_TYPE_LABEL } from '../data/intel';
import { sourceRecoApi } from '../data/sources';
import { useCanManage } from '../pages/Targets';
import { useResource } from '../hooks/useResource';
import { loadPageRecommendations, type PagesData } from './data';
import { groupByCompany, topPages, type PageRecommendation } from './pages';

const MONITOR_PAGE_TYPES = new Set(['HOME', 'PRODUTOS', 'SERVICOS', 'PRECOS', 'CLIENTES', 'CASES', 'VAGAS', 'IMPRENSA', 'BLOG', 'RI', 'OUTRA']);

/** Ativa pelas rotas donas. Devolve quantas ficaram ativas e as mensagens de quem não ativou. */
export async function activatePages(items: readonly PageRecommendation[]): Promise<{ activated: number; failed: string[] }> {
  let activated = 0;
  const failed: string[] = [];
  const recos = items.filter((x) => x.origin.type === 'RECOMENDACAO');
  if (recos.length) {
    try {
      const r = await sourceRecoApi.activate(recos.map((x) => ({ id: (x.origin as { id: string }).id })));
      for (const x of r.results) if (x.outcome === 'ACTIVATED' || x.outcome === 'ALREADY_ACTIVE') activated++; else failed.push(x.message ?? 'Não foi possível ativar uma das páginas.');
    } catch (e) { failed.push(e instanceof ApiError ? e.message : 'Não foi possível ativar as páginas recomendadas.'); }
  }
  for (const x of items) {
    if (x.origin.type !== 'BATTLECARD' || x.needsAuthorization) continue;
    const o = x.origin;
    try {
      await battlecardApi.monitor(o.battlecardId, { kind: o.kind === 'youtube' ? 'youtube' : o.kind === 'feed' ? 'feed' : 'page', url: x.url, ...(o.pageType && MONITOR_PAGE_TYPES.has(o.pageType) ? { pageType: o.pageType as 'BLOG' } : {}), schedule: 'SEMANAL' });
      activated++;
    } catch (e) { failed.push(e instanceof ApiError ? `${x.label}: ${e.message}` : `${x.label}: não foi possível ativar.`); }
  }
  return { activated, failed };
}

function PageRow({ p, canManage, busy, onActivate }: { p: PageRecommendation; canManage: boolean; busy: boolean; onActivate?: (p: PageRecommendation) => void }) {
  return (
    <li className="xp-row">
      <span className="xp-check" aria-hidden="true">✓</span>
      <div className="xp-row-main">
        <b>{p.label}</b>
        <span><span className="xp-tagline">{p.company}</span>{p.pageType ? PAGE_TYPE_LABEL[p.pageType] ?? 'Página' : 'Publicações'} · {p.impact}</span>
        <span>Por que: {p.reason}</span>
        {p.needsAuthorization && <span>Depende de autorização da rede social: conecte a conta em <Link to="/fontes">Fontes</Link> antes de acompanhar.</span>}
        {p.unavailable && <span>A última verificação não conseguiu abrir esta página.</span>}
      </div>
      {onActivate && canManage && !p.needsAuthorization && !p.unavailable && (
        <div className="xp-row-side"><button type="button" className="small-action" disabled={busy} onClick={() => onActivate(p)} aria-label={`Ativar o acompanhamento de ${p.label} (${p.company})`}>Ativar</button></div>
      )}
    </li>
  );
}

export function RecommendedPages({ data, onChanged, headingLevel = 3 }: { data: PagesData; onChanged: () => void; headingLevel?: 2 | 3 }) {
  const canManage = useCanManage();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  const H = headingLevel === 2 ? 'h2' : 'h3';
  const { top, rest } = topPages(data.all);
  const run = async (items: readonly PageRecommendation[]) => {
    setBusy(true);
    const r = await activatePages(items);
    setBusy(false);
    if (r.activated) { setDone(r.activated); toast(r.activated === 1 ? 'Página ativada para acompanhamento.' : `${r.activated} páginas ativadas para acompanhamento.`); }
    if (r.failed.length) toast(r.failed[0]!, 'error');
    onChanged();
  };
  if (!data.all.length) {
    return (
      <div className="xp-pages">
        <H>Páginas para acompanhar</H>
        <p className="xp-quiet">{data.monitored.filter((s) => !s.archivedAt).length ? 'As páginas sugeridas já estão sendo acompanhadas.' : 'Ainda não há páginas sugeridas. Elas aparecem depois da pesquisa dos concorrentes ou da busca de fontes recomendadas.'} <Link to="/fontes?visao=recomendadas">Ver fontes recomendadas</Link></p>
      </div>
    );
  }
  return (
    <div className="xp-pages">
      <H>Páginas para acompanhar</H>
      <p className="xp-quiet">{top.length ? `Encontramos ${data.all.length === 1 ? 'uma fonte' : `${data.all.length} fontes`}. Recomendamos ativar ${top.length === 1 ? 'esta agora' : `estas ${top.length === 2 ? 'duas' : 'três'} agora`}.` : `Encontramos ${data.all.length === 1 ? 'uma fonte' : `${data.all.length} fontes`}, mas nenhuma pode ser ativada direto daqui.`}</p>
      {top.length > 0 && <ul className="xp-list" role="list">{top.map((p) => <PageRow key={p.key} p={p} canManage={canManage} busy={busy} />)}</ul>}
      {top.length > 0 && (canManage
        ? <div className="xp-cta-row"><button type="button" className="xp-primary petrol" disabled={busy || done !== null} aria-busy={busy} onClick={() => void run(top)}>{busy ? 'Ativando…' : done !== null ? `✓ ${done === 1 ? 'Página ativada' : `${done} páginas ativadas`}` : `Ativar ${top.length === 1 ? 'esta recomendação' : `${top.length} recomendações`}`}</button></div>
        : <p className="cell-sub">Um administrador da organização pode ativar o acompanhamento.</p>)}
      {rest.length > 0 && (
        <details className="xp-why xp-more">
          <summary>Ver outras sugestões ({rest.length})</summary>
          {groupByCompany(rest).map((g) => (
            <div key={g.company}>
              <p className="xp-kicker" style={{ marginTop: 12 }}>{g.company}</p>
              <ul className="xp-list" role="list">{g.items.map((p) => <PageRow key={p.key} p={p} canManage={canManage} busy={busy} onActivate={(x) => void run([x])} />)}</ul>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

/**
 * IM-UX-112: em "Páginas monitoradas", as sugestões que já existem (em vez de pedir cadastro manual). Só na nova
 * experiência; desligada, a tela continua igual.
 */
export function MonitoringSuggestions({ onChanged }: { onChanged: () => void }) {
  const { state, refresh } = useResource((s) => loadPageRecommendations(s), []);
  if (state.kind !== 'ready') return null;
  return (
    <article className="card xp-monitor-reco" aria-label="Páginas recomendadas para acompanhar">
      <RecommendedPages data={state.data} onChanged={() => { refresh(); onChanged(); }} headingLevel={2} />
    </article>
  );
}
